import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Loader2, Save, Palette, MessageSquare, Hash, Sparkles, X } from "lucide-react";
import { usePreferences, UserPreferences } from "@/hooks/usePreferences";
import { toast } from "sonner";

const PreferencesForm = () => {
  const { preferences, isLoading, isSaving, savePreferences } = usePreferences();
  const [localPrefs, setLocalPrefs] = useState<UserPreferences | null>(null);
  const [newKeywordInclude, setNewKeywordInclude] = useState("");
  const [newKeywordAvoid, setNewKeywordAvoid] = useState("");

  // Use local state if modified, otherwise use fetched preferences
  const currentPrefs = localPrefs || preferences;

  const updatePref = <K extends keyof UserPreferences>(key: K, value: UserPreferences[K]) => {
    setLocalPrefs((prev) => ({
      ...(prev || preferences),
      [key]: value,
    }));
  };

  const addKeyword = (type: "include" | "avoid") => {
    const keyword = type === "include" ? newKeywordInclude.trim() : newKeywordAvoid.trim();
    if (!keyword) return;

    const key = type === "include" ? "keywords_include" : "keywords_avoid";
    const currentList = currentPrefs[key];
    
    if (!currentList.includes(keyword)) {
      updatePref(key, [...currentList, keyword]);
    }

    if (type === "include") {
      setNewKeywordInclude("");
    } else {
      setNewKeywordAvoid("");
    }
  };

  const removeKeyword = (type: "include" | "avoid", keyword: string) => {
    const key = type === "include" ? "keywords_include" : "keywords_avoid";
    updatePref(key, currentPrefs[key].filter((k) => k !== keyword));
  };

  const handleSave = async () => {
    if (!localPrefs) return;
    
    const success = await savePreferences(localPrefs);
    if (success) {
      toast.success("Preferences saved!");
      setLocalPrefs(null);
    } else {
      toast.error("Failed to save preferences");
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-brand-500" />
          <p className="text-muted-foreground mt-2">Loading preferences...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-brand-100">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Palette className="h-5 w-5 text-brand-500" />
          Content Preferences
        </CardTitle>
        <CardDescription>
          Customize how AI generates your marketing content
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Tone */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
            Tone
          </Label>
          <Select
            value={currentPrefs.tone}
            onValueChange={(value) => updatePref("tone", value as UserPreferences["tone"])}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="professional">Professional - Polished & authoritative</SelectItem>
              <SelectItem value="casual">Casual - Friendly & approachable</SelectItem>
              <SelectItem value="bold">Bold - Confident & punchy</SelectItem>
              <SelectItem value="custom">Custom - Use brand voice below</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Emoji Density */}
        <div className="space-y-2">
          <Label>Emoji Usage</Label>
          <Select
            value={currentPrefs.emoji_density}
            onValueChange={(value) => updatePref("emoji_density", value as UserPreferences["emoji_density"])}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None - No emojis</SelectItem>
              <SelectItem value="minimal">Minimal - 1-2 emojis</SelectItem>
              <SelectItem value="moderate">Moderate - 3-5 emojis</SelectItem>
              <SelectItem value="heavy">Heavy - Emojis throughout</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* CTA Style */}
        <div className="space-y-2">
          <Label>Call-to-Action Style</Label>
          <Select
            value={currentPrefs.cta_style}
            onValueChange={(value) => updatePref("cta_style", value as UserPreferences["cta_style"])}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="soft">Soft - Gentle, low-pressure</SelectItem>
              <SelectItem value="direct">Direct - Clear action-oriented</SelectItem>
              <SelectItem value="question">Question - Engaging question</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Hashtag Count */}
        <div className="space-y-3">
          <Label className="flex items-center gap-2">
            <Hash className="h-4 w-4 text-muted-foreground" />
            Hashtag Count: {currentPrefs.hashtag_count}
          </Label>
          <Slider
            value={[currentPrefs.hashtag_count]}
            onValueChange={(value) => updatePref("hashtag_count", value[0])}
            min={0}
            max={10}
            step={1}
            className="w-full"
          />
          <p className="text-xs text-muted-foreground">
            Number of hashtags to include in social posts
          </p>
        </div>

        {/* Brand Voice */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-muted-foreground" />
            Brand Voice Description
          </Label>
          <Textarea
            placeholder="Describe your brand's unique voice and personality..."
            value={currentPrefs.brand_voice || ""}
            onChange={(e) => updatePref("brand_voice", e.target.value || null)}
            rows={3}
          />
          <p className="text-xs text-muted-foreground">
            Used when tone is set to "Custom"
          </p>
        </div>

        {/* Keywords to Include */}
        <div className="space-y-2">
          <Label>Keywords to Include</Label>
          <div className="flex gap-2">
            <Input
              placeholder="Add keyword..."
              value={newKeywordInclude}
              onChange={(e) => setNewKeywordInclude(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addKeyword("include"))}
            />
            <Button variant="outline" onClick={() => addKeyword("include")}>
              Add
            </Button>
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {currentPrefs.keywords_include.map((keyword) => (
              <Badge key={keyword} variant="secondary" className="gap-1">
                {keyword}
                <button onClick={() => removeKeyword("include", keyword)}>
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        </div>

        {/* Keywords to Avoid */}
        <div className="space-y-2">
          <Label>Keywords to Avoid</Label>
          <div className="flex gap-2">
            <Input
              placeholder="Add keyword..."
              value={newKeywordAvoid}
              onChange={(e) => setNewKeywordAvoid(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addKeyword("avoid"))}
            />
            <Button variant="outline" onClick={() => addKeyword("avoid")}>
              Add
            </Button>
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {currentPrefs.keywords_avoid.map((keyword) => (
              <Badge key={keyword} variant="destructive" className="gap-1">
                {keyword}
                <button onClick={() => removeKeyword("avoid", keyword)}>
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        </div>

        {/* Save Button */}
        {localPrefs && (
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="w-full bg-gradient-to-r from-brand-600 to-brand-500 hover:from-brand-700 hover:to-brand-600"
          >
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save Preferences
              </>
            )}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default PreferencesForm;
